import { getNewAndOldRulesFromURL } from 'src/services/webhooksValidator';

import integration from 'src/lib/api/integration';
import jira from 'src/lib/api/jira';
import { usePromise } from 'src/lib/hooks';

export const useDiffWebhooks = () => {
  return usePromise(async () => {
    const [{ expected_webhooks }, accountsMapping, jiraWebhooks] = await Promise.all([
      integration.getAllExpectedWebhooks(),
      integration.getAccountMapping(),
      jira.getWebhooks(),
    ]);

    const { initRuleValue, newRuleValue } = getNewAndOldRulesFromURL();
    const ruleId = initRuleValue.id || newRuleValue.id;
    const webhooksUsedByOtherRules = expected_webhooks.filter(w => w.used_by_rules.find(r => r.id !== ruleId));

    const tasks = [
      ...pdWebhooksTasks(webhooksUsedByOtherRules, initRuleValue, newRuleValue, accountsMapping),
      ...webhooksSubscriptionsTasks(webhooksUsedByOtherRules, initRuleValue, newRuleValue, accountsMapping),
      ...jiraWebhooksTasks(initRuleValue, newRuleValue, accountsMapping, jiraWebhooks),
      ...jiraIssueUpdateWebhook(initRuleValue, newRuleValue, accountsMapping, jiraWebhooks),
    ];

    return tasks.map(t => t.createTask || t.deleteTask);
  }, []);
};

const JQL_ON_ISSUE_UPDATE = 'issue.property["pagerduty"].incidentId is not empty';

function jiraIssueUpdateWebhook(initRuleValue, newRuleValue, accountsMapping, jiraWebhooks) {
  const isCreate = Boolean(!initRuleValue.id && newRuleValue.id);
  if (!isCreate) return [];

  const existingCreateIssueWebhook = findMatchingJiraWebhook(
    jiraWebhooks,
    `PagerDuty_managed_${accountsMapping.id}_all_rules`,
    ['jira:issue_updated'],
    JQL_ON_ISSUE_UPDATE);

  if (existingCreateIssueWebhook) return [];

  return [{
    key: 'jira_create_all',
    createTask: {
      type: 'jira',
      action: 'create',
      events: ['jira:issue_updated'],
      jql: JQL_ON_ISSUE_UPDATE,
      queryParams: { rule_id: 'all_rules', accounts_mapping_id: accountsMapping.id },
      name: `PagerDuty_managed_${accountsMapping.id}_all_rules`
    }
  }];
}

function pdWebhooksTasks(webhooksUsedByOtherRules, initRuleValue, newRuleValue, accountsMapping) {
  const servicesUsedByOtherRules = webhooksUsedByOtherRules
    .map(w => w && w.service && w.service.id)
    .filter(id => id);

  const oldServices = ((initRuleValue && initRuleValue.config && initRuleValue.config.services) || []);
  const newwServices = ((newRuleValue && newRuleValue.config && newRuleValue.config.services) || []);
  const old = oldServices.map(s => s && s.id).filter(x => x);
  const neww = newwServices.map(s => s && s.id).filter(x => x);

  const summaries = [...oldServices, ...newwServices]
    .filter(s => s)
    .reduce((acc, s) => Object.assign(acc, ({ [s.id]: s.label })), {});

  return [
    ...old.filter(id => !neww.includes(id) && !servicesUsedByOtherRules.includes(id)).map(id => ({
      key: `pd_delete_${id}`,
      deleteTask: {
        type: 'pd',
        action: 'delete',
        service: {
          id,
          summary: summaries[id],
        },
        webhook: {
          name: `JIRA_integration_${accountsMapping.id}_${id}`
        }
      }
    })),

    ...neww.filter(id => !old.includes(id) && !servicesUsedByOtherRules.includes(id)).map(id => ({
      key: `pd_create_${id}`,
      createTask: {
        type: 'pd',
        action: 'create',
        webhookName: `JIRA_integration_${accountsMapping.id}_${id}`,
        webhookConfig: {
          jira_browse_url: accountsMapping.jira && accountsMapping.jira.browse_base_url
        },
        service: {
          id,
          summary: summaries[id],
        }
      }
    }))
  ];
}

function webhooksSubscriptionsTasks(webhooksUsedByOtherRules, initRuleValue, newRuleValue, accountsMapping) {
  const servicesUsedByOtherRules = webhooksUsedByOtherRules
    .map(w => w && w.service && w.service.id)
    .filter(id => id);

  const oldServices = ((initRuleValue && initRuleValue.config && initRuleValue.config.services) || []);
  const newwServices = ((newRuleValue && newRuleValue.config && newRuleValue.config.services) || []);
  const old = oldServices.map(s => s && s.id).filter(x => x);
  const neww = newwServices.map(s => s && s.id).filter(x => x);

  const summaries = [...oldServices, ...newwServices]
    .filter(s => s)
    .reduce((acc, s) => Object.assign(acc, ({ [s.id]: s.label })), {});

  return [
    ...old.filter(id => !neww.includes(id) && !servicesUsedByOtherRules.includes(id)).map(id => ({
      key: `subscription_delete_${id}`,
      deleteTask: {
        type: 'subscription',
        action: 'delete',
        service: {
          id,
          summary: summaries[id],
        },
        webhook: {
          name: `JIRA_integration_${accountsMapping.id}_${id}`
        }
      }
    })),

    ...neww.filter(id => !old.includes(id) && !servicesUsedByOtherRules.includes(id)).map(id => ({
      key: `subscription_create_${id}`,
      createTask: {
        type: 'subscription',
        action: 'create',
        webhookName: `JIRA_integration_${accountsMapping.id}_${id}`,
        webhookConfig: {
          jira_browse_url: accountsMapping.jira && accountsMapping.jira.browse_base_url
        },
        service: {
          id,
          summary: summaries[id],
        }
      }
    }))
  ];
}

function jiraWebhooksTasks(initRuleValue, newRuleValue, accountsMapping, jiraWebhooks) {
  const ruleId = initRuleValue.id || newRuleValue.id;

  const extractFromConfig = rule => {
    const { autocreate_incidents, autocreate_incidents_config, projects } = rule.config || {};
    if (!autocreate_incidents || !Array.isArray(autocreate_incidents_config)) return [];
    const projString = (projects || []).map(p => `"${p.key}"`).sort().join(',');

    return autocreate_incidents_config
      .filter(({ jql, on_create, on_update }) => jql && (on_update || on_create))
      .map(({ jql, on_create, on_update }) => {
        const fullJql = `(${jql}) AND (issue.property["pagerduty"].incidentId is empty) AND (project in (${projString}))`;
        return ({
          key: `${fullJql}_${on_create}_${on_update}`,
          jql: fullJql,
          events: [
            ...(on_create ? ['jira:issue_created'] : []),
            ...(on_update ? ['jira:issue_updated'] : [])
          ].sort()
        });
      })
      .reduce((acc, val) => Object.assign(acc, ({ [val.key]: val })), {});
  };

  const newWebhooks = extractFromConfig(newRuleValue);
  const oldWebhooks = extractFromConfig(initRuleValue);

  return [
    ...Object.values(oldWebhooks).filter(w => !newWebhooks[w.key])
      .map(w => ({
        key: `jira_delete_${w.key}`,
        deleteTask: {
          type: 'jira',
          action: 'delete',
          jql: w.jql,
          events: w.events,
          webhook: {
            id: (findMatchingJiraWebhook(jiraWebhooks, `PagerDuty_managed_${accountsMapping.id}_${ruleId}`, w.events, w.jql) || {}).id
          }
        },
      }))
      .filter(({ deleteTask }) => Boolean(deleteTask.webhook.id)),

    ...Object.values(newWebhooks).filter(w => !oldWebhooks[w.key]).map(w => ({
      key: `jira_create_${w.key}`,
      createTask: {
        type: 'jira',
        action: 'create',
        events: w.events,
        jql: w.jql,
        queryParams: { rule_id: ruleId, accounts_mapping_id: accountsMapping.id },
        name: `PagerDuty_managed_${accountsMapping.id}_${ruleId}`
      },
    })),
  ];
}

function findMatchingJiraWebhook(jiraWebhooks, name, events, jql) {
  const result = jiraWebhooks.find(w => w.name === name
    && w.events.sort().join(',') === events.sort().join()
    && (
      // < Jira 10
      (w.filters && w.filters['issue-related-events-section'] === jql)
      // >= Jira 10
      || (w.configuration && w.configuration.FILTERS && w.configuration.FILTERS === jql)
    )
  );

  if (result && !result.id && result.self) {
    // for Jira versions < 10 the ID has to be extracted from the 'self' URL
    result.id = result.self.split('/').reverse()[0];
  }

  return result;
}
